<template>
  <div class="max-w-4xl mx-auto">
    <div class="bg-white rounded-lg shadow-md p-8">
      <div class="space-y-6">
        <!-- Single File Upload Section -->
        <div class="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
          <div class="space-y-4">
            <div class="flex justify-center">
              <svg class="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
              </svg>
            </div>
            
            <div class="space-y-2">
              <h3 class="text-lg font-semibold text-gray-900">
                Upload Single Skyn Biosensor Data File
              </h3>
              <p class="text-sm text-gray-500">
                Recommended filename format: <strong>&lt;subid&gt;_&lt;dataset_id&gt;</strong> (e.g., 123_001.csv)
              </p>
            </div>
            
            <div class="flex justify-center space-x-4">
              <label for="file-upload" class="cursor-pointer">
                <input
                  id="file-upload"
                  type="file"
                  accept=".csv,.xlsx"
                  @change="handleFileUpload"
                  class="hidden"
                />
                <div class="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                  Choose File
                </div>
              </label>
            </div>
          </div>
        </div>

        <!-- File Selected Display -->
        <div v-if="selectedFile" class="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-3">
              <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <div>
                <h4 class="font-medium text-blue-900">{{ selectedFile.name }}</h4>
                <p class="text-sm text-blue-700">File size: {{ formatFileSize(selectedFile.size) }}</p>
              </div>
            </div>
            <button
              @click="clearSelectedFile"
              class="text-blue-600 hover:text-blue-800 transition-colors"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- Upload Button -->
        <div v-if="selectedFile" class="flex justify-center">
          <button
            @click="uploadFile"
            :disabled="uploading"
            class="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
          >
            <svg v-if="uploading" class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
            </svg>
            <span>{{ uploading ? 'Uploading...' : 'Upload File' }}</span>
          </button>
        </div>

        <!-- Overwrite Confirmation Modal -->
        <BaseModal
          :isVisible="showOverwriteModal"
          title="Overwrite Existing Job?"
          width="md"
          @close="cancelOverwrite"
          @confirm="confirmOverwrite"
        >
          <template #content>
            <p class="text-sm text-gray-700">
              A processed job already exists for <strong>SubID {{ overwriteMetadata?.subid }}</strong>, <strong>Dataset {{ overwriteMetadata?.datasetId }}</strong>.
            </p>
            <p class="text-sm text-gray-500 mt-2">
              Processing this file will <strong>overwrite</strong> the existing job. This action cannot be undone.
            </p>
          </template>
          <template #footer>
            <button
              @click="cancelOverwrite"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-transparent rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              @click="confirmOverwrite"
              class="px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
            >
              Overwrite
            </button>
          </template>
        </BaseModal>

        <!-- Manual SubID/Dataset ID Entry Modal -->
        <SubIdDatasetModal
          :isVisible="showSubIdDatasetModal"
          :filename="selectedFile?.name || ''"
          @close="cancelSubIdDatasetEntry"
          @confirm="confirmSubIdDatasetEntry"
        />

        <!-- Upload Progress -->
        <div v-if="uploading" class="bg-gray-50 rounded-lg p-4">
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-gray-700">Uploading...</span>
            <span class="text-sm text-gray-500">{{ uploadProgress }}%</span>
          </div>
          <div class="w-full bg-gray-200 rounded-full h-2">
            <div class="bg-blue-600 h-2 rounded-full transition-all duration-300" :style="{ width: uploadProgress + '%' }"></div>
          </div>
        </div>

        <!-- Upload Result -->
        <div v-if="uploadResult" class="bg-green-50 border border-green-200 rounded-lg p-4">
          <div class="flex items-center space-x-3">
            <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <div>
              <h4 class="font-medium text-green-900">Upload Successful!</h4>
              <p class="text-sm text-green-700">File has been processed and is ready for analysis.</p>
            </div>
          </div>
        </div>

        <!-- Error Display -->
        <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4">
          <div class="flex items-center space-x-3">
            <svg class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <div>
              <h4 class="font-medium text-red-900">Upload Failed</h4>
              <p class="text-sm text-red-700">{{ error }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { buildApiUrl, API_ENDPOINTS } from '../../config/api.js'
import { processFileForUpload } from '../../utils/excelConverter.js'
import { extractSubidAndDatasetId } from '../../utils/dataUploadConfiguration.js'
import BaseModal from '../../components/BaseModal.vue'
import SubIdDatasetModal from '../../components/SubIdDatasetModal.vue'
import { useLabPath } from '../../composables/useLabPath.js'

export default {
  name: 'SingleFileUpload',
  inject: ['globalState', 'updateGlobalState', 'clearGlobalState', 'setBackButton', 'hideBackButton'],
  components: {
    BaseModal,
    SubIdDatasetModal
  },
  setup() {
    const toLab = useLabPath()
    return { toLab }
  },
  data() {
    return {
      selectedFile: null,
      uploading: false,
      uploadProgress: 0,
      uploadResult: null,
      error: null,
      showOverwriteModal: false,
      overwriteMetadata: null,
      pendingUpload: null, // Store upload function to execute after confirmation
      showSubIdDatasetModal: false,
      pendingMetadata: null // Store metadata entry function to execute after confirmation
    }
  },
  methods: {
    handleFileUpload(event) {
      const file = event.target.files[0]
      if (file) {
        this.selectedFile = file
        this.error = null
        this.uploadResult = null
      }
    },

    clearSelectedFile() {
      this.selectedFile = null
      this.error = null
      this.uploadResult = null
      // Reset file input
      const fileInput = document.getElementById('file-upload')
      if (fileInput) {
        fileInput.value = ''
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    async checkForExistingJob(subid, datasetId) {
      try {
        const sessionId = localStorage.getItem('session_token')
        if (!sessionId) return false

        const response = await axios.post(
          buildApiUrl(API_ENDPOINTS.RESULTS_LIST),
          {
            session_id: sessionId,
            subid: subid,
            dataset_identifier: datasetId
          },
          {
            headers: { 'Content-Type': 'application/json' }
          }
        )

        if (response.data.success && response.data.data && response.data.data.length > 0) {
          return true // Job exists
        }
        return false
      } catch (error) {
        console.warn('Could not check for existing job:', error)
        return false // On error, allow upload to proceed
      }
    },

    async uploadFile() {
      if (!this.selectedFile) return

      // CRITICAL: Clear old session data when starting a new upload
      // This ensures a fresh start and prevents mixing old prior job data with new upload
      console.log('🧹 Clearing old session data for new upload...')
      sessionStorage.removeItem('processingResults')
      sessionStorage.removeItem('configuredData')
      
      // Also clear global state related to prior jobs
      this.clearGlobalState()
      
      console.log('✅ Old session data cleared')

      this.error = null

      try {
        // Extract SubID and Dataset ID from filename for proper naming
        const metadata = extractSubidAndDatasetId(this.selectedFile.name)
        console.log('📋 Extracted metadata from filename:', metadata)

        // Check if extraction failed (metadata is null or missing required fields)
        if (!metadata || !metadata.subid || !metadata.datasetId) {
          // Show modal for manual entry
          this.showSubIdDatasetModal = true
          // Store the function to continue after manual entry
          this.pendingMetadata = (manualMetadata) => this.proceedWithMetadata(manualMetadata)
          return // Don't proceed until user enters metadata
        }

        // Metadata extraction succeeded, proceed with upload
        await this.proceedWithMetadata(metadata)
      } catch (error) {
        console.error('Error in uploadFile:', error)
        this.error = error.message || 'Failed to check for existing job'
      }
    },

    async executeUpload(metadata) {
      this.uploading = true
      this.uploadProgress = 0

      try {
        // Convert Excel files to CSV before upload
        let processedFile = await processFileForUpload(this.selectedFile, metadata)
        
        // For CSV files, ensure the filename matches the expected format
        if (processedFile.name !== `${metadata.subid}_${metadata.datasetId}.csv`) {
          console.log('🏷️ Renaming file to match expected format')
          processedFile = new File([processedFile], `${metadata.subid}_${metadata.datasetId}.csv`, {
            type: processedFile.type,
            lastModified: processedFile.lastModified
          })
        }

        // STEP 1: Request presigned URL from backend
        console.log('📝 Step 1: Requesting presigned URL...')
        const presignedResponse = await axios.post(buildApiUrl(API_ENDPOINTS.UPLOAD_PRESIGNED_URL), {
          session_token: localStorage.getItem('session_token'),
          filename: processedFile.name,
          file_size: processedFile.size
        }, {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true
        })
        
        if (!presignedResponse.data.success) {
          throw new Error('Failed to get upload URL')
        }
        
        const { presigned_url, fields, s3_key, use_direct_upload } = presignedResponse.data
        console.log('✅ Received presigned URL:', { s3_key, use_direct_upload })
        
        // Check if we should use direct upload (local dev mode)
        const shouldUseDirectUpload = !presigned_url || use_direct_upload === true
        
        if (!shouldUseDirectUpload) {
          // STEP 2: Upload directly to S3 (production mode)
          console.log('📤 Step 2: Uploading to S3...')
          const formData = new FormData()
          Object.entries(fields).forEach(([key, value]) => {
            formData.append(key, value)
          })
          formData.append('file', processedFile)
          
          await axios.post(presigned_url, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
              this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            }
          })
          
          console.log('✅ File uploaded to S3:', s3_key)
        } else {
          // Local dev mode: Skip S3 upload, send file content directly
          console.log('📤 Step 2: Using direct upload (local dev mode)...')
          this.uploadProgress = 50 // Show progress
        }
        
        // STEP 3: Notify backend to process the uploaded file
        console.log('⚙️ Step 3: Processing uploaded file...')
        const requestData = shouldUseDirectUpload ? {
          // Local dev: Send file content directly
          file_content: await processedFile.text(),
          filename: processedFile.name,
          subid: metadata.subid,
          dataset_id: metadata.datasetId,
          session_token: localStorage.getItem('session_token')
        } : {
          // Production: Send S3 key
          s3_key: s3_key,
          filename: processedFile.name,
          subid: metadata.subid,
          dataset_id: metadata.datasetId,
          session_token: localStorage.getItem('session_token')
        }

        const response = await axios.post(buildApiUrl(API_ENDPOINTS.CONFIGURE_BIOSENSOR), requestData, {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true
        })

        console.log('🚀 NEW CODE VERSION - Data extraction fix applied!')
        console.log('📊 Upload response:', response.data)
        console.log('🔍 Response data type:', typeof response.data)
        console.log('🔍 Response.data.data type:', typeof response.data.data)
        console.log('🔍 Response.data.data is array:', Array.isArray(response.data.data))
        console.log('🔍 Response.data.data length:', response.data.data?.length)
        
        // Parse the response data if it's a string (JSON)
        let parsedResponseData = response.data
        if (typeof response.data === 'string') {
          try {
            // Handle NaN values in JSON by replacing them with null before parsing
            const sanitizedJson = response.data.replace(/:\s*NaN\s*/g, ': null')
            parsedResponseData = JSON.parse(sanitizedJson)
            console.log('🔧 Parsed JSON response data (sanitized NaN values)')
          } catch (error) {
            console.error('❌ Failed to parse response data as JSON:', error)
            // If JSON parsing fails, try to handle it as a raw string error
            this.error = `Upload failed - invalid response format: ${error.message}`
            this.uploading = false
            return
          }
        }
        
        console.log('✅ Success flag:', parsedResponseData.success)
        console.log('🔍 File type being processed:', this.selectedFile.name.endsWith('.xlsx') ? 'Excel' : 'CSV')
        console.log('🔍 Original file name:', this.selectedFile.name)
        console.log('🔍 Processed file name:', processedFile.name)

        // Check if we have data (successful processing)
        // The response structure is: {data: [array], headers: [...], success: true, ...}
        // The data array is directly in parsedResponseData.data, not nested
        console.log('🔍 parsedResponseData structure:', {
          hasData: !!parsedResponseData.data,
          dataType: typeof parsedResponseData.data,
          isArray: Array.isArray(parsedResponseData.data),
          dataLength: parsedResponseData.data?.length,
          dataKeys: parsedResponseData.data ? Object.keys(parsedResponseData.data) : 'no data'
        })
        
        // Backend returns: {success: true, data: [...], headers: [...], session_id: ..., ...}
        // The data array is directly at parsedResponseData.data, not nested
        const configuredRows = parsedResponseData.data  // Configured (not fully processed)
        console.log('🔍 Extracted configured rows:', configuredRows)
        console.log('🔍 Is array?', Array.isArray(configuredRows))
        console.log('🔍 Length:', configuredRows?.length)
        console.log('🔍 Data stored in S3:', parsedResponseData?.data_stored_in_s3)
        console.log('🔍 Data S3 key:', parsedResponseData?.data_s3_key)
        
        // Check if data is stored in S3 (large datasets) or returned directly (small datasets)
        const isDataInS3 = parsedResponseData?.data_stored_in_s3 === true
        
        if (isDataInS3 || (configuredRows && Array.isArray(configuredRows))) {
          console.log('✅ SUCCESS: Data validation passed' + (isDataInS3 ? ' (data in S3)' : ' (data in response)'))
          // Sort by 'datetime' if present
          let sortedData = configuredRows
          if (Array.isArray(configuredRows) && configuredRows.length > 0 && typeof configuredRows[0] === 'object') {
            const timeKey = 'datetime'
            if (Object.prototype.hasOwnProperty.call(configuredRows[0], timeKey)) {
              const toMillis = (val) => {
                if (val == null) return Number.NaN
                if (typeof val === 'number') {
                  return val < 1e12 ? val * 1000 : val
                }
                const parsed = Date.parse(val)
                return Number.isNaN(parsed) ? Number.NaN : parsed
              }
              sortedData = [...configuredRows].sort((a, b) => toMillis(a[timeKey]) - toMillis(b[timeKey]))
              console.log('🕒 Sorted configured data by key:', timeKey)
            }
          }
          this.uploadResult = sortedData
          
          // Extract summary data from the response
          // parsedResponseData has: {data, headers, processed_rows, session_id, etc.} at top level
          
          console.log('🔍 summaryData from backend:', parsedResponseData)
          console.log('🔍 session_id from backend:', parsedResponseData?.session_id)
          
          // Reorder headers so left-most columns are: datetime, TAC, Temperature_C, Motion
          const desiredLeftMost = ['datetime', 'TAC', 'Temperature_C', 'Motion']
          const incomingHeaders = parsedResponseData?.headers || []
          const leftMostExisting = desiredLeftMost.filter(h => incomingHeaders.includes(h))
          const remaining = incomingHeaders.filter(h => !leftMostExisting.includes(h))
          const reorderedHeaders = [...leftMostExisting, ...remaining]

          // Create configuredData object with both raw data and summary information
          // Ensure sampling rates are numbers for comparison
          const initialRate = Number(parsedResponseData?.initial_sampling_rate) || 0
          const finalRate = Number(parsedResponseData?.final_sampling_rate) || 0
          
          console.log('🔍 Sampling rate values from API:', {
            initial_sampling_rate: parsedResponseData?.initial_sampling_rate,
            final_sampling_rate: parsedResponseData?.final_sampling_rate,
            initialRate: initialRate,
            finalRate: finalRate,
            areEqual: initialRate === finalRate
          })

          // NOTE: For new uploads, we don't preserve prior_job_s3_key because this is a fresh upload
          // prior_job_s3_key is only relevant when reprocessing an existing prior job (handled in SubjectDashboard.vue)
          // For new uploads, we start with a clean slate
          let priorJobS3Key = null
          
          // Create fresh configuredData for new upload (no prior job flags)
          const configuredDataWithSummary = {
            data: sortedData, // The array of records sorted by time (empty for large datasets)
            headers: reorderedHeaders,
            finalSamplingRate: finalRate,
            configuredRows: parsedResponseData?.processed_rows || 0,
            dayCount: parsedResponseData?.day_count || 0,
            originalRows: parsedResponseData?.original_rows || 0,
            initialSamplingRate: initialRate,
            filename: parsedResponseData?.filename || '',
            session_id: parsedResponseData?.session_id || null,
            subid: parsedResponseData?.subid || metadata.subid || '',
            dataset_id: parsedResponseData?.dataset_id || metadata.datasetId || '',
            dataset_identifier: parsedResponseData?.dataset_id || metadata.datasetId || '', // Alias for compatibility
            data_stored_in_s3: isDataInS3 || false,
            data_s3_key: parsedResponseData?.data_s3_key || null,
            // Explicitly set prior job flags to false/null for new uploads
            is_prior_job: false,
            prior_job_s3_key: null,
            s3_key: null  // No s3_key for new uploads (only for prior jobs)
          }
          
          // Store the fresh configuredData in sessionStorage
          sessionStorage.setItem('configuredData', JSON.stringify(configuredDataWithSummary))
          console.log('✅ Stored fresh configuredData for new upload:', {
            session_id: configuredDataWithSummary.session_id?.substring(0, 20) + '...' || null,
            subid: configuredDataWithSummary.subid,
            dataset_id: configuredDataWithSummary.dataset_id,
            is_prior_job: false
          })

          // Update global state with the complete configuredData
          this.updateGlobalState({
            configuredData: configuredDataWithSummary,
            subid: metadata.subid || '',
            datasetId: metadata.datasetId || '',
            sessionId: parsedResponseData?.session_id || null
          })

          // Navigate to dashboard after successful upload
          setTimeout(() => {
            this.$router.push(this.toLab('/subject-dashboard'))
          }, 1500)
          } else if (parsedResponseData && parsedResponseData.success === false) {
            console.log('❌ Backend reported failure:', parsedResponseData.message)
            this.error = parsedResponseData.message || 'Upload failed'
          } else {
            console.log('❌ No valid data found in response')
            console.log('❌ configuredRows:', configuredRows)
            this.error = 'Upload failed - no data received'
          }
      } catch (error) {
        console.error('Upload error:', error)
        console.error('Error response data:', error.response?.data)
        console.error('Error response status:', error.response?.status)
        this.error = error.response?.data?.message || `Upload failed with ${error.response?.status} error. Please try again.`
      } finally {
        this.uploading = false
      }
    },

    confirmOverwrite() {
      this.showOverwriteModal = false
      if (this.pendingUpload) {
        this.pendingUpload()
        this.pendingUpload = null
      }
      this.overwriteMetadata = null
    },

    cancelOverwrite() {
      this.showOverwriteModal = false
      this.pendingUpload = null
      this.overwriteMetadata = null
    },

    async proceedWithMetadata(metadata) {
      // Check if a job already exists for this subid-dataset_id combination
      const jobExists = await this.checkForExistingJob(metadata.subid, metadata.datasetId)
      
      if (jobExists) {
        // Show confirmation modal
        this.overwriteMetadata = metadata
        this.showOverwriteModal = true
        // Store the upload function to execute after confirmation
        this.pendingUpload = () => this.executeUpload(metadata)
        return // Don't proceed until user confirms
      }

      // No existing job, proceed with upload
      await this.executeUpload(metadata)
    },

    confirmSubIdDatasetEntry(manualMetadata) {
      this.showSubIdDatasetModal = false
      if (this.pendingMetadata) {
        this.pendingMetadata(manualMetadata)
        this.pendingMetadata = null
      }
    },

    cancelSubIdDatasetEntry() {
      this.showSubIdDatasetModal = false
      this.pendingMetadata = null
      this.error = 'Upload cancelled - SubID and Dataset ID are required'
    }
  },
  mounted() {
    // Set up back button to return to upload choice
    this.setBackButton({
      text: 'Back to Upload Options',
      route: '/'
    })
  }
}
</script>
